import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Content,
  Header,
  Table,
  TableColumn,
  Progress,
  TableProps,
  CreateButton,
} from '@backstage/core-components';
import {
  Typography,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  TextField,
  InputAdornment,
  debounce,
  Box,
  Popover,
  MenuList,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import enCA from 'date-fns/locale/en-CA';
import frCA from 'date-fns/locale/fr-CA';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import MoreIcon from '@material-ui/icons/MoreHoriz';
import {
  useApi,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@material-ui/icons/Search';
import './ViewContract.css';
import {
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { useEpDevPNotifications } from '@solace-labs/backstage-plugin-ep-devp-react';
import { BackstageTheme } from '@backstage/theme';
import { EpDevPConfirmDeleteContractDialog } from './EpDevPConfirmDeleteContractDialog';
import { DatePicker } from '@material-ui/pickers';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';
import { tablePaginationLocalization } from '../../i18n/pagination';

// Style definitions
const useStyles = makeStyles((theme) => ({
  root: {
    "& $actions": {
      paddingRight: theme.spacing(1),
    },
    "& table $actions": {
      paddingRight: theme.spacing(0),
    },
  },
  actions: {
    display: "flex",
    justifyContent: "right"
  },
  menuList: {
    padding: theme.spacing(0),
    '&:focus': {
      outline: "none",
    },
  },
  menuAction: {
    fontSize: "inherit",
  },
  clickableRow: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  cellText: {
    fontSize: theme.typography.body2.fontSize,
    fontFamily: theme.typography.fontFamily,
  },
  headerText: {
    fontWeight: theme.typography.fontWeightBold,
  },
  noDataMessage: {
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "center",
  },
}));

interface Contract {
  uuid: string;
  contractnumber: string;
  contractname: string;
  contractstatus: string;
  subscriptionid: string;
  startdate: string;
  enddate: string;
  createddate: string;
  createdby: string;
  updateddate: string;
  updatedby: string;
  subscriptionname: string;
  applicationname: string;
}

type UserRoleResponse = {
  userID: string;
  roleID: string;
  roleName: string;
};

type TableOptions = NonNullable<TableProps<Contract>['options']>;

type TableRowAction = {
  title: string;
  icon?: React.ReactNode;
  onClick: (rowData: Contract) => void;
};

// Action Menu component
const TableRowActionMenu = ({
  actions,
  anchorElement,
  open,
  onClose,
  onExecuteAction,
}: {
  actions: TableRowAction[];
  anchorElement: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onExecuteAction: (action: TableRowAction) => void;
}) => {
  const classes = useStyles();

  return (
    <Popover
      anchorEl={anchorElement}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={open}
      onClose={() => onClose()}
    >
      <MenuList className={classes.menuList} variant="menu" autoFocus>
        {actions?.map((action) => (
          <MenuItem
            key={action.title}
            className={classes.menuAction}
            onClick={() => onExecuteAction(action)}
          >
            {action.icon && <Box mr={1} display="flex" alignItems="center">{action.icon}</Box>}
            {action.title}
          </MenuItem>
        ))}
      </MenuList>
    </Popover>
  );
};

const ContractsList = () => {
  const { t, locale, formatUtcDate, formatUtcDateTime } =
    useServiceRegistryTranslation();
  const classes = useStyles();
  const theme = useTheme<BackstageTheme>();
  const config = useApi(configApiRef);
  const baseUrl = config.getString('backend.baseUrl');
  const navigate = useNavigate();
  const identityApi = useApi(identityApiRef);
  
  // State variables
  const [userRole, setUserRole] = useState<UserRoleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [startDateRange, setStartDateRange] = useState<MaterialUiPickersDate>(null);
  const [endDateRange, setEndDateRange] = useState<MaterialUiPickersDate>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [displayedQuery, setDisplayedQuery] = useState('');
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const createNotification = useEpDevPNotifications();
  const [snackbarSeverity, setSnackbarSeverity] = useState<'info' | 'error'>('info');
  const [statusFilter, setStatusFilter] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Sorting state
  const [orderBy, setOrderBy] = useState<string>('contractname');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  
  // Action menu state
  const [selectedRow, setSelectedRow] = useState<Contract | undefined>();
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [isMoreActionsMenuOpen, setIsMoreActionsMenuOpen] = useState<boolean>(false);

  // Force re-render trigger for table
  const [tableKey, setTableKey] = useState(0);

  // Fetch user role on component mount
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const profile = await identityApi.getProfileInfo();
        const { token } = await identityApi.getCredentials();
        const response = await fetch(`${baseUrl}/api/users/Role`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: profile.email }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user role: ${response.statusText}`);
        }

        const userRoleResponse: UserRoleResponse = await response.json();
        setUserRole(userRoleResponse);
      } catch (error) {
        console.error('Error fetching user role:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchUserRole();
  }, [identityApi, baseUrl]);

  // Fetch contracts function
  const fetchContracts = useCallback(async () => {
    if (!userRole) {
      
      return;
    }
    
    
    setLoading(true);
    try {
      const currentPage = page + 1;
      const params = new URLSearchParams({
        pageNumber: currentPage.toString(),
        pageSize: rowsPerPage.toString(),
        sortFieldName: orderBy,
        sortDirection: orderDirection,
      });

      // Add optional parameters
      if (displayedQuery.trim()) {
        params.append('searchTerm', displayedQuery.trim());
      }
      if (startDateRange) {
        const formattedStartDate = startDateRange instanceof Date
          ? startDateRange.toISOString().split('T')[0]
          : '';
        if (formattedStartDate) {
          params.append('startDateRange', formattedStartDate);
        }
      }
      if (endDateRange) {
        const formattedEndDate = endDateRange instanceof Date
          ? endDateRange.toISOString().split('T')[0]
          : '';
        if (formattedEndDate) {
          params.append('endDateRange', formattedEndDate);
        }
      }
      if (statusFilter) {
        params.append('statusFilter', statusFilter);
      }

      const url = `${baseUrl}/api/contract/view?${params.toString()}`;
      
      const { token } = await identityApi.getCredentials();
      const response = await fetch(url, { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        } 
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch contracts: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      
      setContracts(data.contracts || []);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      console.error('❌ Error fetching contracts:', error);
      setContracts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    userRole,
    page,
    rowsPerPage,
    orderBy,
    orderDirection,
    displayedQuery,
    startDateRange,
    endDateRange,
    statusFilter,
    identityApi,
    baseUrl,
  ]);

  // Fetch contracts when dependencies change
  useEffect(() => {

    
    if (userRole) {
      fetchContracts();
    }
  }, [fetchContracts]);

  // Date range change handlers
  const handleDateRangeChange = (
    name: 'startDateRange' | 'endDateRange'
  ) => (date: MaterialUiPickersDate) => {
   
    if (name === 'startDateRange') {
      setStartDateRange(date);
    } else {
      setEndDateRange(date);
    }
    setPage(0);
  };

  // Status filter change handler
  const handleStatusFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    
    setStatusFilter(value);
    setPage(0);
  };

  // Edit handler
  const handleEdit = (contract: Contract) => {
    navigate(`/contract/edit/${contract.uuid}`);
  };

  // Row click handler
  const handleRowClick = (contract: Contract) => {
    if (userRole?.roleName === 'Contract Authority' && contract.contractstatus !== 'Deleted') {
      handleEdit(contract);
    }
  };

  // Manual sort handler - this is the KEY FIX
  const handleColumnSort = (field: string) => {
    
    
    let newDirection: 'asc' | 'desc' = 'asc';
    
    if (orderBy === field) {
      // If clicking the same column, toggle direction
      newDirection = orderDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // If clicking a different column, start with asc
      newDirection = 'asc';
    }
    
    
    
    setOrderBy(field);
    setOrderDirection(newDirection);
    setPage(0);
    setTableKey(prev => prev + 1); // Force table re-render
  };

  // Action menu handlers
  const openMoreActionsMenu = (event: React.MouseEvent<HTMLElement>, data: Contract) => {
    event.stopPropagation();
    setSelectedRow(data);
    setActionsMenuAnchor(event.currentTarget);
    setIsMoreActionsMenuOpen(true);
  };

  const closeMoreActionsMenu = () => {
    setActionsMenuAnchor(null);
    setIsMoreActionsMenuOpen(false);
  };

  const handleAction = (action: TableRowAction) => {
    if (selectedRow) {
      action.onClick(selectedRow);
    }
    closeMoreActionsMenu();
  };

  // Delete handlers
  const handleDeleteConfirmed = async () => {
    if (!selectedRow) return;
    try {
      const { token } = await identityApi.getCredentials();
      const response = await fetch(
        `${baseUrl}/api/contract/delete/${selectedRow.uuid}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const result = await response.json();
      if (response.ok) {
        await fetchContracts();
        setSnackbarMessage(t('contracts.deletedSuccess'));
        createNotification(t('contracts.deletedSuccess'));
        setSnackbarSeverity('info');
      } else {
        setSnackbarMessage(t('contracts.deleteFailed'));
        createNotification(t('contracts.deleteFailed'), 'warning');
        setSnackbarSeverity('error');
      }
    } catch (error) {
      console.error('Failed to delete contract:', error);
      setSnackbarMessage(t('contracts.deleteFailed'));
      createNotification(t('contracts.deleteFailed'), 'warning');
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setIsConfirmDeleteDialogOpen(false);
    }
  };

  // Search handlers
  const debouncedSetSearchQuery = useCallback(
    debounce((query: string) => {
     
      setDisplayedQuery(query);
      setPage(0);
    }, 600),
    []
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalSearchQuery(value);
    setSearchQuery(value);
    debouncedSetSearchQuery(value);
  };

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  const handleSearchFocus = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Row actions for the menu
  const actions: TableRowAction[] = [
    {
      title: t('contracts.editAction'),
      icon: <EditIcon fontSize="small" />,
      onClick: handleEdit,
    },
    {
      title: t('contracts.deleteAction'),
      icon: <DeleteIcon fontSize="small" />,
      onClick: () => setIsConfirmDeleteDialogOpen(true),
    },
  ];

  // Render cell with consistent font styling
  const renderCell = (value: string | null | undefined) => (
    <Typography className={classes.cellText}>
      {value || '-'}
    </Typography>
  );

  const renderStatus = (value: string | null | undefined) => {
    const statusKeys: Record<string, string> = {
      'Not Started': 'contracts.notStarted',
      'In Progress': 'contracts.inProgress',
      Signed: 'contracts.signed',
      Expired: 'contracts.expired',
      Cancelled: 'contracts.cancelled',
      Deleted: 'contracts.deleted',
    };
    return renderCell(value ? t(statusKeys[value] || value) : value);
  };

  // Column definitions with manual sort handlers
  const columns: TableColumn<Contract>[] = [
    {
      title: (
        <Typography 
          className={classes.headerText} 
          style={{ cursor: 'pointer' }}
          onClick={() => handleColumnSort('contractname')}
        >
          {t('contracts.name')} {orderBy === 'contractname' && (orderDirection === 'asc' ? '↓' : '↑')}
        </Typography>
      ),
      field: 'contractname',
      sorting: false, // Disable built-in sorting
      render: (row: Partial<Contract>) => (
        <Typography
          className={`${classes.cellText} ${userRole?.roleName === 'Contract Authority' && row.contractstatus !== 'Deleted' ? classes.clickableRow : ""}`}
          onClick={() => row && handleRowClick(row as Contract)}
        >
          {row.contractname || '-'}
        </Typography>
      ),
    },
    {
      title: (
        <Typography 
          className={classes.headerText} 
          style={{ cursor: 'pointer' }}
          onClick={() => handleColumnSort('contractnumber')}
        >
          {t('contracts.number')} {orderBy === 'contractnumber' && (orderDirection === 'asc' ? '↓' : '↑')}
        </Typography>
      ),
      field: 'contractnumber',
      sorting: false,
      render: (row: Partial<Contract>) => renderCell(row.contractnumber),
    },
    {
      title: (
        <Typography 
          className={classes.headerText} 
          style={{ cursor: 'pointer' }}
          onClick={() => handleColumnSort('applicationname')}
        >
          {t('contracts.application')} {orderBy === 'applicationname' && (orderDirection === 'asc' ? '↓' : '↑')}
        </Typography>
      ),
      field: 'applicationname',
      sorting: false,
      render: (row: Partial<Contract>) => renderCell(row.applicationname),
    },
    {
      title: (
        <Typography 
          className={classes.headerText} 
          style={{ cursor: 'pointer' }}
          onClick={() => handleColumnSort('subscriptionname')}
        >
          {t('contracts.subscription')} {orderBy === 'subscriptionname' && (orderDirection === 'asc' ? '↓' : '↑')}
        </Typography>
      ),
      field: 'subscriptionname',
      sorting: false,
      render: (row: Partial<Contract>) => renderCell(row.subscriptionname),
    },
    {
      title: (
        <Typography 
          className={classes.headerText} 
          style={{ cursor: 'pointer' }}
          onClick={() => handleColumnSort('contractstatus')}
        >
          {t('contracts.status')} {orderBy === 'contractstatus' && (orderDirection === 'asc' ? '↓' : '↑')}
        </Typography>
      ),
      field: 'contractstatus',
      sorting: false,
      render: (row: Partial<Contract>) => renderStatus(row.contractstatus),
    },
    {
      title: (
        <Typography 
          className={classes.headerText} 
          style={{ cursor: 'pointer' }}
          onClick={() => handleColumnSort('startdate')}
        >
          {t('contracts.startDate')} {orderBy === 'startdate' && (orderDirection === 'asc' ? '↓' : '↑')}
        </Typography>
      ),
      field: 'startdate',
      sorting: false,
      render: (row: Partial<Contract>) => renderCell(row.startdate ? formatUtcDate(row.startdate) : row.startdate),
    },
    {
      title: (
        <Typography 
          className={classes.headerText} 
          style={{ cursor: 'pointer' }}
          onClick={() => handleColumnSort('enddate')}
        >
          {t('contracts.endDate')} {orderBy === 'enddate' && (orderDirection === 'asc' ? '↓' : '↑')}
        </Typography>
      ),
      field: 'enddate',
      sorting: false,
      render: (row: Partial<Contract>) => renderCell(row.enddate ? formatUtcDate(row.enddate) : row.enddate),
    },
    {
      title: (
        <Typography 
          className={classes.headerText} 
          style={{ cursor: 'pointer' }}
          onClick={() => handleColumnSort('createdby')}
        >
          {t('contracts.createdBy')} {orderBy === 'createdby' && (orderDirection === 'asc' ? '↓' : '↑')}
        </Typography>
      ),
      field: 'createdby',
      sorting: false,
      render: (row: Partial<Contract>) => renderCell(row.createdby),
    },
    {
      title: (
        <Typography 
          className={classes.headerText} 
          style={{ cursor: 'pointer' }}
          onClick={() => handleColumnSort('createddate')}
        >
          {t('contracts.createdDate')} {orderBy === 'createddate' && (orderDirection === 'asc' ? '↓' : '↑')}
        </Typography>
      ),
      field: 'createddate',
      sorting: false,
      render: (row: Partial<Contract>) => renderCell(row.createddate ? formatUtcDateTime(row.createddate, true) : row.createddate),
    },
    {
      title: (
        <Typography 
          className={classes.headerText} 
          style={{ cursor: 'pointer' }}
          onClick={() => handleColumnSort('updatedby')}
        >
          {t('contracts.updatedBy')} {orderBy === 'updatedby' && (orderDirection === 'asc' ? '↓' : '↑')}
        </Typography>
      ),
      field: 'updatedby',
      sorting: false,
      render: (row: Partial<Contract>) => renderCell(row.updatedby),
    },
    {
      title: (
        <Typography 
          className={classes.headerText} 
          style={{ cursor: 'pointer' }}
          onClick={() => handleColumnSort('updateddate')}
        >
          {t('contracts.updatedDate')} {orderBy === 'updateddate' && (orderDirection === 'asc' ? '↓' : '↑')}
        </Typography>
      ),
      field: 'updateddate',
      sorting: false,
      render: (row: Partial<Contract>) => renderCell(row.updateddate ? formatUtcDateTime(row.updateddate, true) : row.updateddate),
    },
  ];

  // Add action column for Contract Authority users
  if (userRole?.roleName === 'Contract Authority') {
    columns.push({
      title: '',
      field: 'actions',
      sorting: false,
      render: (row: Contract) => (
        row.contractstatus !== 'Deleted' ? (
          <IconButton
            onClick={(event) => openMoreActionsMenu(event, row)}
            size="small"
          >
            <MoreIcon />
          </IconButton>
        ) : (
          <span>-</span>
        )
      ),
    });
  }

  // Table options - disable built-in sorting
  const options: TableOptions = {
    paging: true,
    pageSize: rowsPerPage,
    pageSizeOptions: [10, 20, 50, 100],
    search: false,
    filtering: false,
    emptyRowsWhenPaging: false,
    padding: 'dense',
    actionsColumnIndex: -1,
    headerStyle: {
      fontWeight: theme.typography.fontWeightBold,
    },
    sorting: false, // Disable built-in sorting completely
    loadingType: 'linear',
  };

  // Loading states
  if (initialLoading) {
    return <Progress />;
  }

  return (
    <div className={classes.root}>
      <Header title={t('contracts.title')} />
      <Content>
        {userRole?.roleName === 'Contract Authority' && (
          <Box
            mr={2}
            style={{
              width: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              textAlign: 'left',
              alignItems: 'center',
              marginBottom: '16px',
              flexDirection: 'row',
              justifyContent: 'flex-end'
            }}
          >
            <CreateButton
              title={t('contracts.add')}
              to={'/contract/add'}
            />
          </Box>
        )}

        <div className="contract-article">
          <div className="contract-filter-class">
            <MuiPickersUtilsProvider
              utils={DateFnsUtils}
              locale={locale === 'fr-CA' ? frCA : enCA}
            >
              <DatePicker
                label={t('contracts.startDate')}
                value={startDateRange}
                onChange={handleDateRangeChange('startDateRange')}
                format="MM/dd/yyyy"
                variant="inline"
                inputVariant="outlined"
                style={{ marginTop: '0px', marginRight: '1rem' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" size="small">
                        <CalendarTodayIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                size='small'
                PopoverProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
              />
              <DatePicker
                label={t('contracts.endDate')}
                value={endDateRange}
                onChange={handleDateRangeChange('endDateRange')}
                format="MM/dd/yyyy"
                variant="inline"
                inputVariant="outlined"
                style={{ marginTop: '0px', marginRight: '1rem' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" size="small">
                        <CalendarTodayIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                size='small'
                PopoverProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
              />
            </MuiPickersUtilsProvider>
            <FormControl variant="outlined" style={{ marginRight: '1rem' }} size='small'>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                displayEmpty
              >
                <MenuItem value="">{t('contracts.allStatuses')}</MenuItem>
                <MenuItem value="Not Started">{t('contracts.notStarted')}</MenuItem>
                <MenuItem value="In Progress">{t('contracts.inProgress')}</MenuItem>
                <MenuItem value="Signed">{t('contracts.signed')}</MenuItem>
                <MenuItem value="Cancelled">{t('contracts.cancelled')}</MenuItem>
                <MenuItem value="Expired">{t('contracts.expired')}</MenuItem>
                <MenuItem value="Deleted">{t('contracts.deleted')}</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="contract-search-bar">
            <TextField
              placeholder={t('contracts.search')}
              variant="standard"
              value={localSearchQuery}
              onChange={handleSearchChange}
              inputRef={searchInputRef}
              onFocus={handleSearchFocus}
              style={{ width: '100%' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>

        <Table<Contract>
          key={tableKey} // Force re-render when sort changes
          options={options}
          data={contracts}
          columns={columns}
          totalCount={totalCount}
          page={page}
          onPageChange={newPage => {
            
            setPage(newPage);
          }}
          onRowsPerPageChange={newRowsPerPage => {
            
            setRowsPerPage(newRowsPerPage);
            setPage(0);
          }}
          isLoading={loading}
          localization={{
            pagination: tablePaginationLocalization(t),
            body: {
              emptyDataSourceMessage: loading ? (
                <div></div>
              ) : (
                <Typography color="textSecondary" className={classes.noDataMessage}>
                  {t('contracts.noContracts')}
                </Typography>
              )
            },
          }}
        />

        {/* Action Menu Popover */}
        <TableRowActionMenu
          actions={actions}
          anchorElement={actionsMenuAnchor}
          open={isMoreActionsMenuOpen}
          onClose={closeMoreActionsMenu}
          onExecuteAction={handleAction}
        />

        {/* Delete Confirmation Dialog */}
        {selectedRow && (
          <EpDevPConfirmDeleteContractDialog
            contract={selectedRow}
            open={isConfirmDeleteDialogOpen}
            onCancel={() => setIsConfirmDeleteDialogOpen(false)}
            onConfirmDelete={handleDeleteConfirmed}
          />
        )}
      </Content>
    </div>
  );
};

export default ContractsList;
